/**
 * Identitas pelanggan di nota (batch N1, brief owner 22 Sep 2026) — CERMIN
 * `backend/routers/identitas_nota.py`.
 *
 * Berkas ini SENGAJA MURNI: tanpa satu pun `import`. Tes kesetaraan
 * (`backend/tests/test_n1_identitas_js_sama.py`) menyalin berkas ini ke folder
 * sementara lalu menjalankannya lewat `node` — import `react`/`./api` tidak
 * bisa di-resolve di sana, dan tes yang gagal dijalankan cenderung di-skip.
 * Hook pengambil aturan dari server ada di `hooks/useAturanIdentitasNota.js`.
 *
 * Server tetap hakimnya. Layar hanya mencegat lebih awal supaya kasir tidak
 * baru tahu ditolak setelah menekan "Kirim ke Kasir".
 */

export const ATURAN_BAWAAN = { hp_cabang_wajib: true, kartu_wajib: "b2b_user_bertempo" };
// Sama dengan `identitas_nota.BOLEH_BUAT_KARTU_NOTA` — server juga mengirim
// daftar ini (`GET /sales/config/identitas-nota`); bawaan ini hanya dipakai
// sebelum/kalau permintaan itu gagal.
export const IZIN_BUAT_KARTU_BAWAAN = [["pelanggan", "create"], ["nota_sales", "create"], ["sales", "create"]];
// Sama dengan `pelanggan.BOLEH_CARI_PELANGGAN_NOTA`.
export const IZIN_CARI_KARTU_BAWAAN = [["pelanggan", "view"], ["nota_sales", "create"], ["sales", "create"]];

const PANJANG_HP = { "628": [11, 14], "62": [10, 15], lain: [10, 15] };
const KARAKTER_HP_SAH = "0123456789+-.() ";
const DIGIT = "0123456789";
// Himpunan spasi Python (`str.isspace` / `\s` pada str) — ditulis eksplisit
// karena `\s` JavaScript berbeda (mengenal U+FEFF, tidak mengenal \x1c-\x1f
// dan \x85). Kunci nama yang berbeda antara layar dan server berarti layar
// menyatakan "cocok kartu" untuk nama yang server tolak.
const SPASI_PY = "[\\t\\n\\v\\f\\r\\x1c-\\x20\\x85\\xa0\\u1680\\u2000-\\u200a\\u2028\\u2029\\u202f\\u205f\\u3000]";
const RE_SPASI_PY = new RegExp(`${SPASI_PY}+`, "g");
const RE_TEPI_PY = new RegExp(`^${SPASI_PY}+|${SPASI_PY}+$`, "g");

/** Buang karakter format tak terlihat (Unicode Cf — ikut tersalin dari
 *  WhatsApp) dan jadikan spasi Unicode spasi biasa. */
export function bersihkanHp(raw) {
  return String(raw ?? "")
    .replace(/\p{Cf}/gu, "")
    .replace(/[\p{Zs}\p{Zl}\p{Zp}\t\r\n\v\f]/gu, " ")
    .replace(/^ +| +$/g, "");
}

/** 08xx / 8xx -> 628xx (cermin `services/wa_service.normalisasi_hp` untuk digit ASCII). */
export function normalisasiHp(digit) {
  const d = [...String(digit ?? "")].filter(ch => DIGIT.includes(ch)).join("");
  if (!d) return "";
  if (d.startsWith("0")) return "62" + d.slice(1);
  if (d.startsWith("8")) return "62" + d;
  return d;
}

/** { sah, pesan, simpan, normal } — kosong dianggap sah dengan simpan "" (wajib-tidaknya diputuskan pemanggil). */
export function periksaBentukHp(raw) {
  const teks = bersihkanHp(raw);
  if (!teks) return { sah: true, pesan: null, simpan: "", normal: "" };
  const tolak = (pesan) => ({ sah: false, pesan, simpan: "", normal: "" });
  const plus = [...teks].filter(ch => ch === "+").length;
  if ([...teks].some(ch => !KARAKTER_HP_SAH.includes(ch)) || plus > 1 || (plus === 1 && !teks.startsWith("+"))) {
    return tolak(`No HP "${teks}" tidak sah — hanya boleh angka (boleh diawali +, boleh spasi, tanda hubung, titik, atau kurung). Contoh: 0812 3456 7890`);
  }
  const digit = [...teks].filter(ch => DIGIT.includes(ch)).join("");
  if (!digit) return tolak(`No HP "${teks}" tidak berisi angka.`);
  if (teks.startsWith("+") && digit.startsWith("0")) {
    return tolak(`No HP "${teks}" salah format: sesudah + harus kode negara (mis. +62 812…), bukan angka 0. Tulis 0812… atau +62 812….`);
  }
  const normal = normalisasiHp(digit);
  if (normal.startsWith("620")) {
    return tolak(`No HP "${teks}" salah format: sesudah 62 jangan ada angka 0 (tulis 0812… atau +62 812…).`);
  }
  let batas; let contoh;
  if (normal.startsWith("628")) {
    batas = PANJANG_HP["628"]; contoh = "HP Indonesia 10–13 angka termasuk 0 di depan, contoh 0812 3456 7890";
  } else if (normal.startsWith("62")) {
    batas = PANJANG_HP["62"]; contoh = "telepon Indonesia minimal 9 angka termasuk 0 di depan, contoh 0561 712345";
  } else {
    batas = PANJANG_HP.lain; contoh = "nomor luar negeri minimal 10 angka termasuk kode negara, contoh +65 9123 4567";
  }
  if (normal.length < batas[0]) return tolak(`No HP "${teks}" terlalu pendek — ${contoh}.`);
  if (normal.length > batas[1]) return tolak(`No HP "${teks}" terlalu panjang — ${contoh}.`);
  return { sah: true, pesan: null, simpan: (teks.startsWith("+") ? "+" : "") + digit, normal };
}

const normalLonggar = (raw) => normalisasiHp(bersihkanHp(raw));

/** Edit ini MENGUBAH No HP tersimpan? (cermin `identitas_nota.hp_berubah`).
 *  Digit sama dalam bentuk lain bukan perubahan; kalau keduanya tanpa digit
 *  (isian lama "-" / "tidak ada" vs kolom dikosongkan) teks bersihnya yang
 *  dibandingkan — mengosongkan "-" ADALAH perubahan, jadi layar menagih No HP
 *  alih-alih server diam-diam menulis kembali "-". */
export function hpBerubah(lama, baru) {
  const a = normalLonggar(lama); const b = normalLonggar(baru);
  if (a !== b) return true;
  if (a) return false;
  return bersihkanHp(lama) !== bersihkanHp(baru);
}

/** Kunci nama = kunci surat Tagih Sekaligus (`piutang._kunci_nama_pelanggan`). */
export function kunciNama(nama) {
  return String(nama ?? "").replace(RE_TEPI_PY, "").toLowerCase().replace(RE_SPASI_PY, " ");
}

/** `" ".join(nama.split())` versi Python. */
export function rapikanNama(nama) {
  return String(nama ?? "").replace(RE_TEPI_PY, "").replace(RE_SPASI_PY, " ");
}

/** Nota ini WAJIB memakai nama dari kartu pelanggan? (cermin `kartu_wajib_untuk`). */
export function kartuWajibUntuk(jenis, tempo, aturan) {
  const lingkup = (aturan || ATURAN_BAWAAN).kartu_wajib;
  if (lingkup === "mati") return false;
  const j = String(jenis || "umum").trim().toLowerCase() || "umum";
  if (j !== "b2b" && j !== "user") return false;
  if (lingkup === "b2b_user_semua") return true;
  return !!String(tempo || "").trim();
}

/** Nama nota ini WAJIB dipilih dari kartu SEKARANG? (cermin `identitas_nota.butuh_kartu`).
 *  `baru`/`lama` = objek bermedan nama_pelanggan/jenis_pelanggan/tempo_pembayaran;
 *  `lama` null = nota baru. Nota bertempo lama bernama bebas yang cuma diedit
 *  harga/catatannya tidak ditagih kartu. */
export function butuhKartu(baru, lama, aturan) {
  const b = baru || {};
  if (!kartuWajibUntuk(b.jenis_pelanggan, b.tempo_pembayaran, aturan)) return false;
  if (!lama) return true;
  return kunciNama(lama.nama_pelanggan) !== kunciNama(b.nama_pelanggan)
    || !kartuWajibUntuk(lama.jenis_pelanggan, lama.tempo_pembayaran, aturan);
}

/** { bolehCari, bolehBuat } untuk kolom nama nota (cermin `boleh_lihat_kartu`/
 *  `boleh_buat_kartu`). `can(modul, aksi)` dari AuthContext; daftar kunci dari
 *  server (`GET /sales/config/identitas-nota`), bawaan kalau belum termuat.
 *  "+ Pelanggan baru" butuh izin buat DAN izin cari — akun yang cuma memegang
 *  `pelanggan.create` akan membuat kartu yang tidak bisa ia pakai (403) dan
 *  melihat nama + No HP kartu lain lewat jawaban 409. */
export function izinKartu(aturan, can) {
  const punya = (daftar) => (daftar || []).some(([m, a]) => !!can(m, a));
  const bolehCari = punya(aturan?.izin_cari_kartu || IZIN_CARI_KARTU_BAWAAN);
  const bolehBuat = bolehCari && punya(aturan?.izin_buat_kartu || IZIN_BUAT_KARTU_BAWAAN);
  return { bolehCari, bolehBuat };
}

/** Nomor kartu (tersimpan 628xx) ditampilkan seperti ketikan kasir biasa (08xx). */
export function hpTampilan(hp) {
  const s = String(hp || "");
  return s.startsWith("628") ? "0" + s.slice(2) : s;
}

/** Ejaan cabang sesuai daftar pilihan (tanpa beda huruf besar-kecil).
 *
 *  Isian otomatis form nota diambil dari data karyawan APA ADANYA; kalau
 *  ejaannya beda huruf dengan dropdown, yang terkirim bukan pilihan dropdown
 *  dan server menggantinya diam-diam. Tidak ketemu = dikembalikan apa adanya:
 *  server yang memutuskan (dan menolaknya dengan daftar pilihan). */
export function cabangKanonikDariOpsi(raw, opsi) {
  const v = String(raw || "").trim();
  if (!v) return "";
  const cocok = (opsi || []).find(o => o && String(o).trim().toLowerCase() === v.toLowerCase());
  return cocok || v;
}
